// Typed fetch client for DESIGN.md section 7. One function per endpoint the kiosk uses.
// Swapped out for src/mock.ts when VITE_MOCK=1 — see src/backend.ts.
import type {
  TournamentFormat,
  RollCallInteractionPayload,
  VoiceConfirmationInteractionPayload,
  VoiceProfile,
  VoiceSampleOut,
  VoiceSamplePayload,
  ApiErrorPayload,
  HeadToHeadResponse,
  IdentityResolveResponse,
  Match,
  MatchType,
  MatchTypeCode,
  Player,
  PlayerDetail,
  RankingsResponse,
  RatingPool,
  Side,
  TableState,
  Tournament,
  TournamentDetail,
  TtsCacheEntry,
  TtsEvent,
  TtsTemplate,
  TtsVoice,
  VersusResponse,
  VoiceTrainingProgress,
  VoiceTrainingProgressPayload,
} from './types'

export class ApiRequestError extends Error {
  readonly code: string
  readonly status: number

  constructor(payload: ApiErrorPayload, status: number) {
    super(payload.error)
    this.name = 'ApiRequestError'
    this.code = payload.code
    this.status = status
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response
  try {
    res = await fetch(`/api${path}`, {
      ...init,
      headers: {
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      },
    })
  } catch (err) {
    // Network-level failure (backend down, DNS, offline) — surface a stable code so the
    // UI can show the "backend unreachable" banner regardless of cause.
    throw new ApiRequestError({ error: 'Network error', code: 'network_error' }, 0)
  }

  if (!res.ok) {
    let payload: ApiErrorPayload
    try {
      payload = await res.json()
    } catch {
      payload = { error: res.statusText || 'Request failed', code: 'unknown_error' }
    }
    throw new ApiRequestError(payload, res.status)
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

// 7.1 Identity
export function identityResolve(scan: string): Promise<IdentityResolveResponse> {
  return request('/identity/resolve', { method: 'POST', body: JSON.stringify({ scan }) })
}

// 7.2 Players
export function getPlayers(q = '', limit = 50): Promise<{ items: Player[] }> {
  const params = new URLSearchParams()
  if (q) params.set('q', q)
  params.set('limit', String(limit))
  return request(`/players?${params.toString()}`)
}

export function createGuest(displayName: string): Promise<Player> {
  return request('/players/guest', { method: 'POST', body: JSON.stringify({ displayName }) })
}

export function getPlayer(id: number): Promise<PlayerDetail> {
  return request(`/players/${id}`)
}

// 7.3 Match types
export function getMatchTypes(): Promise<{ items: MatchType[] }> {
  return request('/match-types')
}

// 7.4 Matches
export interface CreateMatchPayload {
  matchTypeCode: MatchTypeCode
  bestOf: number
  leftPlayerId: number
  rightPlayerId: number
  firstServer?: Side
  table?: string
  serveEvery?: number // 2026-08-31 user feedback item 2 — omitted defaults server-side to 3
  // 2026-08-31 round-8 feedback item 21 — set when this match was started from a Tournament
  // bracket slot's "start" action (store/draft.ts's prefillFromTournamentSlot); drives that
  // slot's winner auto-advancing in the bracket once the match finishes.
  tournamentSlotId?: number
  // Round-31 (2026-08-31) — read from settings.fastMode at submit time, not draft state
  // (it's a device preference, not a per-match form field). Omitted defaults to false
  // server-side, same as every pre-existing caller.
  fastMode?: boolean
}

export function createMatch(payload: CreateMatchPayload): Promise<Match> {
  return request('/matches', { method: 'POST', body: JSON.stringify(payload) })
}

export interface GetMatchesParams {
  status?: string
  table?: string
  playerId?: number
  limit?: number
  offset?: number
}

export function getMatches(params: GetMatchesParams = {}): Promise<{ items: Match[]; total: number }> {
  const qs = new URLSearchParams()
  if (params.status) qs.set('status', params.status)
  if (params.table) qs.set('table', params.table)
  if (params.playerId != null) qs.set('playerId', String(params.playerId))
  qs.set('limit', String(params.limit ?? 50))
  qs.set('offset', String(params.offset ?? 0))
  return request(`/matches?${qs.toString()}`)
}

export function getMatch(id: number): Promise<Match> {
  return request(`/matches/${id}`)
}

export function postPoint(matchId: number, side: Side, source: 'touch' | 'admin' | 'voice' = 'touch'): Promise<Match> {
  return request(`/matches/${matchId}/points`, { method: 'POST', body: JSON.stringify({ side, source }) })
}

export function undoPoint(matchId: number, source: 'touch' | 'admin' = 'touch'): Promise<Match> {
  return request(`/matches/${matchId}/undo`, { method: 'POST', body: JSON.stringify({ source }) })
}

export function abandonMatch(matchId: number): Promise<Match> {
  return request(`/matches/${matchId}/abandon`, { method: 'POST' })
}

export function swapSides(matchId: number): Promise<Match> {
  return request(`/matches/${matchId}/swap-sides`, { method: 'POST' })
}

// Round-31 (2026-08-31) — fast mode's only scoring call: finalize the CURRENT game with its
// two final scores (DESIGN.md §23). Server validates the pair against the match type's
// pointsToWin/winBy regardless of what the kiosk already checked (`code: 'invalid_score'`).
export function finalizeGameFast(matchId: number, leftScore: number, rightScore: number): Promise<Match> {
  return request(`/matches/${matchId}/finalize-game`, { method: 'POST', body: JSON.stringify({ leftScore, rightScore }) })
}

// 7.5 Tables
export function getTable(table: string): Promise<TableState> {
  return request(`/tables/${table}`)
}

// 7.7 Rankings
export function getRankings(pool: RatingPool): Promise<RankingsResponse> {
  return request(`/rankings?pool=${pool}`)
}

export function getVersus(a: number, b: number): Promise<VersusResponse> {
  return request(`/rankings/vs?a=${a}&b=${b}`)
}

export function getHeadToHead(a: number, b: number): Promise<HeadToHeadResponse> {
  return request(`/rankings/head-to-head?a=${a}&b=${b}`)
}

// 7.10 Tournaments
export function createTournament(name: string, format: TournamentFormat = 'single_elim', prize: string | null = null, matchTypeCode: MatchTypeCode = 'rapid11', bestOf = 3): Promise<Tournament> {
  return request('/tournaments', { method: 'POST', body: JSON.stringify({ name, format, prize, matchTypeCode, bestOf }) })
}

export function getTournaments(status?: string): Promise<{ items: Tournament[] }> {
  return request(`/tournaments${status ? `?status=${status}` : ''}`)
}

export function getTournament(id: number): Promise<TournamentDetail> {
  return request(`/tournaments/${id}`)
}

export function addTournamentParticipant(tournamentId: number, playerId: number, registeredVia: 'scan' | 'manual' = 'manual'): Promise<TournamentDetail> {
  return request(`/tournaments/${tournamentId}/participants`, { method: 'POST', body: JSON.stringify({ playerId, registeredVia }) })
}

export function removeTournamentParticipant(tournamentId: number, playerId: number): Promise<TournamentDetail> {
  return request(`/tournaments/${tournamentId}/participants/${playerId}`, { method: 'DELETE' })
}

export function generateBracket(tournamentId: number): Promise<TournamentDetail> {
  return request(`/tournaments/${tournamentId}/generate-bracket`, { method: 'POST' })
}

// 7.11 Voice (round-15) — samples corpus + per-player fingerprint profiles.
export function postVoiceSample(payload: VoiceSamplePayload): Promise<VoiceSampleOut> {
  return request('/voice/samples', { method: 'POST', body: JSON.stringify(payload) })
}

export function getVoiceProfiles(): Promise<{ items: VoiceProfile[] }> {
  return request('/voice/profiles')
}

export function rebuildVoiceProfile(playerId: number): Promise<VoiceProfile> {
  return request(`/voice/profiles/${playerId}/rebuild`, { method: 'POST' })
}

// Item 4 (round-18): "Train my voice" resume-on-reload progress.
export function getTrainingProgress(playerId: number): Promise<{ items: VoiceTrainingProgress[] }> {
  return request(`/voice/training-progress/${playerId}`)
}

export function putTrainingProgress(payload: VoiceTrainingProgressPayload): Promise<VoiceTrainingProgress> {
  return request('/voice/training-progress', { method: 'PUT', body: JSON.stringify(payload) })
}

// Round-27 (2026-08-31) — corner-claim voice-confirmation observability.
// See app/services/tts_service.py — audio/wav, not JSON, so this builds the URL rather than
// going through request(); the caller (services/confirmFlow.ts) sets it as an <audio> src
// directly and treats a fetch/decode/play failure as "TTS unavailable right now" (204 or any
// other failure), never blocking the confirmation flow.
export function voiceConfirmNameTtsUrl(playerId: number, lang: string): string {
  return `/api/voice/tts/confirm-name/${playerId}?lang=${encodeURIComponent(lang)}`
}

export function postVoiceConfirmationInteraction(payload: VoiceConfirmationInteractionPayload): Promise<void> {
  return request('/voice/confirmations', { method: 'POST', body: JSON.stringify(payload) })
}

// Round-29 (2026-08-31) — tournament roll-call. Same audio/wav-URL-not-JSON convention as
// voiceConfirmNameTtsUrl above; services/rollCall.ts treats any failure as "TTS unavailable".
export function rollCallTtsUrl(playerId: number, lang: string): string {
  return `/api/voice/tts/roll-call/${playerId}?lang=${encodeURIComponent(lang)}`
}

export function postRollCallInteraction(payload: RollCallInteractionPayload): Promise<void> {
  return request('/voice/roll-call', { method: 'POST', body: JSON.stringify(payload) })
}

// ---------------------------------------------------------------------------
// Round-30 (2026-09-01) — TTS debug panel (views/DebugTts.vue). See
// server/app/routers/voice.py's "TTS debug panel" section. The URL builders below (not
// request()) mirror voiceConfirmNameTtsUrl/rollCallTtsUrl above — DebugTts.vue itself does the
// fetch (it needs raw response headers + timing, not just the decoded body) rather than going
// through request().
// ---------------------------------------------------------------------------

export function getTtsVoices(): Promise<{ items: TtsVoice[] }> {
  return request('/voice/tts/voices')
}

export function getTtsTemplates(): Promise<{ items: TtsTemplate[] }> {
  return request('/voice/tts/templates')
}

export function ttsPreviewUrl(templateId: string, playerId: number, lang: string, voice?: string | null): string {
  const params = new URLSearchParams({ lang })
  if (voice) params.set('voice', voice)
  return `/api/voice/tts/preview/${encodeURIComponent(templateId)}/${playerId}?${params}`
}

export function ttsSpeakUrl(text: string, lang: string, voice?: string | null): string {
  const params = new URLSearchParams({ text, lang })
  if (voice) params.set('voice', voice)
  return `/api/voice/tts/speak?${params}`
}

export function getTtsCache(): Promise<{ items: TtsCacheEntry[] }> {
  return request('/voice/tts/cache')
}

export function ttsCacheAudioUrl(cacheId: string): string {
  return `/api/voice/tts/cache/${encodeURIComponent(cacheId)}/audio`
}

export function purgeTtsCacheEntry(cacheId: string, adminToken: string): Promise<{ purged: number }> {
  return request(`/voice/tts/cache/${encodeURIComponent(cacheId)}`, {
    method: 'DELETE',
    headers: { 'X-Admin-Token': adminToken },
  })
}

export function purgeAllTtsCache(adminToken: string): Promise<{ purged: number }> {
  return request('/voice/tts/cache', { method: 'DELETE', headers: { 'X-Admin-Token': adminToken } })
}

export function getTtsEvents(limit = 50): Promise<{ items: TtsEvent[] }> {
  return request(`/voice/tts/events?limit=${limit}`)
}
